import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import { ColoredIcon } from 'base/ui/colored_icon';
import {
  IconArrowForward,
  IconChangeTone,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconDelete,
  IconImproveWriting,
  IconInfo,
  IconMakeLonger,
  IconMakeShorter,
  IconNumberOfLines,
  IconRefresh,
  IconSimplify,
  IconStars,
} from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import {
  placeFixedWidthAroundAnchor,
  useOnMousedownOutside,
} from 'base/ui/menu';
import { AnyMenuItem, menuItem, subMenu } from 'base/ui/menu_model';
import { Scrollable } from 'base/ui/scrollable';
import { usePinElementPlacement } from 'base/ui/tooltip';
import { TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { NodeMeasurements } from 'blocks/component_renderer/node_measuring';
import {
  globalTextSuggestionsStore,
  SectionTextEditingModel,
} from 'blocks/component_renderer/text_overrides';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { createPortal } from 'react-dom';
import ReactFocusLock from 'react-focus-lock';
import {
  makeTextFittingPrompt,
  Range,
  RewritingTask,
  TextRewritingAi,
  TextRewritingLocation,
} from '../ai/text_rewriting';
import styles from './text_rewriting_ui.css';

type RewritingPrompt = {
  goal: string;
  label: string;
  icon?: React.ComponentType;
};

const TOP_PROMPTS: RewritingPrompt[] = [
  {
    icon: IconRefresh,
    label: 'Rewrite',
    goal: 'rephrase the text, but keep the same meaning.',
  },
  {
    icon: IconMakeShorter,
    label: 'Make shorter',
    goal: 'make the text shorter.',
  },
  {
    icon: IconMakeLonger,
    label: 'Make longer',
    goal: 'make the text longer.',
  },
];
const BOTTOM_PROMPTS: RewritingPrompt[] = [
  {
    icon: IconCheck,
    label: 'Fix spelling & grammar',
    goal: 'fix spelling and grammar mistakes.',
  },
  {
    icon: IconImproveWriting,
    label: 'Improve writing',
    goal: 'improve the quality of the writing.  Make it sound more professional.',
  },
  {
    icon: IconSimplify,
    label: 'Simplify language',
    goal: 'make the text easier to understand.  Use simpler words & shorter sentences.',
  },
];

// Professional, casual, straightforward, confident, friendly.
const CHANGE_TONE_PROMPTS: RewritingPrompt[] = [
  {
    label: 'Professional',
    goal: 'change the tone of the text to be more professional.',
  },
  { label: 'Casual', goal: 'change the tone of the text to be more casual.' },
  {
    label: 'Straightforward',
    goal: 'change the tone of the text to be more straightforward.',
  },
  {
    label: 'Confident',
    goal: 'change the tone of the text to be more confident.',
  },
  {
    label: 'Friendly',
    goal: 'change the tone of the text to be more friendly.',
  },
];

export const startRewritingMenu = computedFn(
  (
    model: SectionTextEditingModel,
    location: TextRewritingLocation,
    measurements: NodeMeasurements,
    text: string,
    range: Range,
  ): AnyMenuItem[] => {
    const ai = model.textRewriting;
    if (!ai) return [];

    const makeOnClick = (prompt: string) => () => {
      ai.start(measurements, location, text, range, prompt);
    };

    const mapPrompt = (pr: RewritingPrompt) =>
      menuItem({
        label: pr.label,
        icon: pr.icon ? (
          <ColoredIcon icon={pr.icon} color="colorPurple" />
        ) : undefined,
        onClick: makeOnClick(pr.goal),
      });

    const isFullRange = range.start <= 0 && range.end >= text.length;

    return [
      ...TOP_PROMPTS.map(mapPrompt),
      subMenu({
        label: 'Make number of lines',
        icon: <ColoredIcon icon={IconNumberOfLines} color="colorPurple" />,
        disabled: !isFullRange,
        disabledReason: 'Select the whole text box to use this option.',
        menu: [1, 2, 3, 4].map((n) => {
          const prompt = makeTextFittingPrompt(n);
          return menuItem({
            label: prompt,
            searchSynonyms: `Make ${n} lines`,
            onClick: makeOnClick(prompt),
          });
        }),
      }),
      ...BOTTOM_PROMPTS.map(mapPrompt),
      subMenu({
        label: 'Change tone',
        icon: <ColoredIcon icon={IconChangeTone} color="colorPurple" />,
        menu: CHANGE_TONE_PROMPTS.map(mapPrompt),
      }),
    ];
  },
);

export const TextRewritingProgress = observer(function _TextRewritingProgress({
  location,
  ai,
  anchorTo,
}: {
  location: TextRewritingLocation;
  ai: TextRewritingAi;
  anchorTo: React.RefObject<HTMLElement>;
}) {
  const [panelFixedCount, setPanelFixed] = React.useState(0);
  const tasks = ai.getAll(location);
  const [index, setIndex] = React.useState<number>(0);
  const task = tasks[Math.min(index, tasks.length - 1)];

  const startTask = React.useCallback(
    (basisText: string, range: Range, nextPrompt: string) => {
      if (!task) return;
      const newTask = ai.start(
        task.containerMeasurements,
        task.location,
        basisText,
        range,
        nextPrompt,
      );
      setIndex(ai.getAll(location).indexOf(newTask));
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ai, task],
  );

  const onClose = React.useCallback(() => {
    ai.removeAll(location);
  }, [ai, location]);

  if (tasks.length === 0) return null;
  return (
    <FloatingPanel
      anchorTo={anchorTo}
      onClose={onClose}
      panelFixed={panelFixedCount > 0}
    >
      <TaskProgress
        key={keyOfObject(task)}
        task={task}
        onClose={onClose}
        startTask={startTask}
        setPanelFixed={setPanelFixed}
      />
      {tasks.length > 1 && (
        <div className={styles.history}>
          <Inline spacing={0} yalign="center">
            <TextTinyLight>History</TextTinyLight>
            <SpacerGrow />
            <Button
              size="small"
              variant="subtle"
              icon={IconChevronLeft}
              tooltip="View previous"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
            />
            <TextTinyLight>
              {index + 1} of {tasks.length}
            </TextTinyLight>
            <Button
              size="small"
              variant="subtle"
              icon={IconChevronRight}
              tooltip="View next"
              disabled={index >= tasks.length - 1}
              onClick={() => setIndex(index + 1)}
            />
          </Inline>
        </div>
      )}
    </FloatingPanel>
  );
});

const TaskProgress = observer(function _TaskProgress({
  task,
  onClose,
  startTask,
  setPanelFixed,
}: {
  task: RewritingTask;
  onClose(): void;
  startTask(basisText: string, range: Range, nextPrompt: string): void;
  setPanelFixed: React.Dispatch<React.SetStateAction<number>>;
}) {
  const state = task.state;
  const contextBefore = task.originalText.slice(0, task.range.start);
  const contextAfter = task.originalText.slice(task.range.end);

  const [nextPrompt, setNextPrompt] = React.useState('');

  const runNextPrompt = React.useCallback(() => {
    if (nextPrompt === '') return;
    if (state.type !== 'fulfilled') return;
    const { newText } = state;
    const start = contextBefore.length;
    startTask(
      contextBefore + newText + contextAfter,
      { start, end: start + newText.length },
      nextPrompt,
    );
  }, [startTask, nextPrompt, contextBefore, contextAfter, state]);

  const reRun = React.useCallback(() => {
    startTask(task.originalText, task.range, task.prompt);
  }, [startTask, task]);

  const acceptSuggestion = React.useCallback(() => {
    if (task.state.type !== 'fulfilled') return;
    const { element, nodeId } = task.location;
    element.textOverrides.set(
      nodeId,
      contextBefore + task.state.newText + contextAfter,
    );
    onClose();
  }, [task, contextBefore, contextAfter, onClose]);

  useKeyboardShortcuts(state.type !== 'fulfilled', {
    // Escape is handled by FloatingPanel
    [shortcut('R')]: reRun,
  });

  const isTypingNextPrompt = state.type === 'fulfilled' && nextPrompt !== '';
  const actionButtons = (
    <>
      {state.type === 'pending' && (
        <Button size="small" label="Stop" shortcut="Esc" onClick={onClose} />
      )}
      <Button
        size="small"
        label="Re-run"
        labelNoWrap
        icon={state.type === 'pending' ? undefined : IconRefresh}
        shortcut={state.type === 'pending' ? 'R' : undefined}
        onClick={reRun}
      />
      {state.type === 'fulfilled' && (
        <Button
          size="small"
          icon={IconDelete}
          label="Discard"
          onClick={onClose}
        />
      )}
      {state.type === 'fulfilled' && (
        <AcceptSuggestionButton
          onClose={onClose}
          value={contextBefore + state.newText + contextAfter}
          location={task.location}
          setPanelFixed={setPanelFixed}
        />
      )}
    </>
  );

  return (
    <div className={styles.taskProgress}>
      <Scrollable>
        <div className={styles.output}>
          {state.type === 'rejected' ? (
            <TextSmallLight color="red">{state.error}</TextSmallLight>
          ) : (
            <Rows spacing={8}>
              <div>
                <TextSmallLight tag="span">{state.newText}</TextSmallLight>
                <div
                  className={classNames(
                    styles.aiCursor,
                    state.type === 'fulfilled' && styles.isHidden,
                  )}
                />
              </div>
              {state.apology && (
                <TextSmallLight color="grey">{state.apology}</TextSmallLight>
              )}
            </Rows>
          )}
        </div>
      </Scrollable>
      <Inline spacing={8} yalign="center">
        <div className={styles.nameplate}>
          {state.type === 'pending' && <PendingNameplate />}
          {state.type === 'rejected' && <ErrorNameplate />}
          {state.type === 'fulfilled' && (
            <NextPromptInput
              value={nextPrompt}
              onChange={setNextPrompt}
              onSubmit={runNextPrompt}
              acceptSuggestion={acceptSuggestion}
            />
          )}
        </div>
        {isTypingNextPrompt ? null : actionButtons}
      </Inline>
    </div>
  );
});

function AcceptSuggestionButton({
  onClose,
  value,
  location,
  setPanelFixed,
}: {
  onClose(): void;
  value: string;
  location: TextRewritingLocation;
  setPanelFixed: React.Dispatch<React.SetStateAction<number>>;
}) {
  const { element, nodeId } = location;
  const removeSuggestion = React.useRef<() => void>();

  const refInline = React.useRef<HTMLButtonElement>(null);
  const refOverlay = React.useRef<HTMLDivElement>(null);

  const visualProps = {
    size: 'small',
    variant: 'primary',
    background: 'ai',
    icon: IconCheck,
    label: 'Replace',
  } as const;

  React.useEffect(() => {
    let destroyed = false;
    const update = () => {
      const inline = refInline.current;
      const overlay = refOverlay.current;
      if (!inline || !overlay) return;
      if (destroyed) return;
      const inlineRect = inline.getBoundingClientRect();
      overlay.style.transform = `translate(${inlineRect.x}px, ${inlineRect.y}px)`;
      requestAnimationFrame(update);
    };
    update();
    return () => void (destroyed = true);
  }, []);

  return (
    <>
      <Button
        ref={refInline}
        {...visualProps}
        onClick={() => {
          removeSuggestion.current?.();
          setPanelFixed(0);
          element.textOverrides.set(nodeId, value);
          onClose();
        }}
        onMouseEnter={() => {
          removeSuggestion.current?.();
          // The new suggestion might be a different length, which would cause layout shifting :/
          setPanelFixed((x) => x + 1);
          removeSuggestion.current = globalTextSuggestionsStore.addSuggestion(
            element,
            nodeId,
            value,
          );
        }}
        onMouseLeave={() => {
          removeSuggestion.current?.();
          requestAnimationFrame(() => {
            // Wait for the suggestion to be removed before unfixing the panel.
            setPanelFixed((x) => Math.max(0, x - 1));
          });
        }}
      />
      {/* We want everything except this button to fade out.  So we fade the panel
      and overlay a full opacity button on top (with pointer-events none). */}
      {createPortal(
        <div ref={refOverlay} className={styles.acceptSuggestionButtonOverlay}>
          <Button {...visualProps} />
        </div>,
        document.body,
      )}
    </>
  );
}

function PendingNameplate() {
  return (
    <Inline spacing={4} yalign="center">
      <div className={styles.aiStarsSmall}>
        <IconStars />
      </div>
      <TextSmallLight color="purple">AI is writing...</TextSmallLight>
    </Inline>
  );
}

function ErrorNameplate() {
  return (
    <Inline spacing={4} yalign="center">
      <div className={styles.errorIconSmall}>
        <IconInfo />
      </div>
      <TextSmallLight color="red">Error rewriting</TextSmallLight>
    </Inline>
  );
}
function NextPromptInput({
  value,
  onChange,
  onSubmit,
  acceptSuggestion,
}: {
  value: string;
  onChange(v: string): void;
  onSubmit(): void;
  acceptSuggestion(): void;
}) {
  return (
    <FormInput
      value={value}
      onChange={onChange}
      iconStart={<ColoredIcon color="colorPurple" icon={IconStars} />}
      buttonEnd={
        value ? (
          <Button
            size="small"
            variant="primary"
            background="ai"
            icon={IconArrowForward}
            tooltip="Run prompt"
            onClick={onSubmit}
          />
        ) : undefined
      }
      placeholder="Tell AI what to do next..."
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (value) {
            onSubmit();
          } else {
            acceptSuggestion();
          }
        }
      }}
      type="textarea"
      variant="borderless"
    />
  );
}

function FloatingPanel({
  anchorTo,
  children,
  onClose,
  panelFixed,
}: {
  anchorTo: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  onClose: () => void;
  panelFixed: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  usePinElementPlacement(
    true,
    anchorTo,
    ref,
    panelFixed ? noop : placeFixedWidthAroundAnchor,
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  return createPortal(
    <ReactFocusLock persistentFocus={true}>
      <div
        className={classNames(
          styles.floatingPanel,
          panelFixed && styles.isFixed,
        )}
        ref={ref}
        onKeyDown={onKeyDown}
        {...useOnMousedownOutside(true, onClose)}
      >
        <Rows spacing={0}>{children}</Rows>
      </div>
    </ReactFocusLock>,
    document.body,
  );
}

const noop = () => {};
